import React, { useContext, useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import { useTenant } from "../contexts/TenantContext";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  ArrowLeft, Building2, Calendar, MapPin, User, 
  ClipboardList, FileText, Clock, CheckCircle, ClipboardCheck, Calculator 
} from "lucide-react";
import ManDayCalculator from "../components/ManDayCalculator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuditDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { tenantSlug } = useTenant();
  const [audit, setAudit] = useState(null);
  const [reports, setReports] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAudit = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/audits/${id}`, { withCredentials: true });
      setAudit(response.data);
    } catch (error) {
      toast.error("Denetim bulunamadı");
      navigate("/audits");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchReports = useCallback(async () => {
    try {
      // Fetch both regular reports and interactive reports
      const [regularRes, interactiveRes] = await Promise.all([
        axios.get(`${API}/reports`, { withCredentials: true }),
        axios.get(`${API}/interactive-reports`, { withCredentials: true })
      ]);
      
      const regularReports = regularRes.data.filter(r => r.audit_id === id).map(r => ({ ...r, type: 'regular' }));
      const interactiveReports = interactiveRes.data.filter(r => r.audit_id === id).map(r => ({ ...r, type: 'interactive' }));
      
      setReports([...regularReports, ...interactiveReports]);
    } catch (error) {
      console.error("Reports error:", error);
    }
  }, [id]);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/templates`, { withCredentials: true });
      setTemplates(response.data);
    } catch (error) {
      console.error("Templates error:", error);
    }
  }, []);

  useEffect(() => {
    fetchAudit();
    fetchReports();
    fetchTemplates();
  }, [fetchAudit, fetchReports, fetchTemplates]);

  const handleStatusChange = async (status) => {
    try {
      await axios.patch(`${API}/audits/${id}/status?status=${status}`, {}, { withCredentials: true });
      toast.success("Durum güncellendi");
      fetchAudit();
    } catch (error) {
      toast.error("Durum güncellenemedi");
    }
  };

  // Adam-gün hesaplama sonucunu kaydet (hem denetime hem müşteriye)
  const handleManDayCalculation = async (result) => {
    try {
      // 1. Denetimi güncelle
      await axios.patch(
        `${API}/audits/${id}`,
        {
          audit_duration_days: result.base_manday,
          final_duration_days: result.final_manday,
          risk_level: result.risk_level
        },
        { withCredentials: true }
      );

      // 2. Müşteriyi güncelle (eğer müşteri varsa)
      if (audit?.customer_id) {
        await axios.patch(
          `${API}/customers/${audit.customer_id}/manday`,
          {
            manday: result.final_manday,
            risk_level: result.risk_level,
            room_count: result.room_count
          },
          { withCredentials: true }
        );
      }

      toast.success(`Adam-gün kaydedildi: ${result.final_manday} A/G`);
      fetchAudit(); // Sayfayı yenile
    } catch (error) {
      console.error("Adam-gün kaydetme hatası:", error);
      toast.error("Adam-gün kaydedilemedi");
    }
  };

  const downloadTemplate = async (templateId, fileName) => {
    try {
      const response = await axios.get(`${API}/templates/${templateId}/download`, {
        withCredentials: true,
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Şablon indirildi");
    } catch (error) {
      toast.error("İndirme başarısız");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      planned: "bg-blue-100 text-blue-800",
      in_progress: "bg-yellow-100 text-yellow-800",
      completed: "bg-green-100 text-green-800",
      cancelled: "bg-gray-100 text-gray-800"
    };
    const labels = {
      planned: "Planlandı",
      in_progress: "Devam Ediyor",
      completed: "Tamamlandı",
      cancelled: "İptal"
    };
    return (
      <span className={`px-3 py-1 text-sm rounded-full ${styles[status] || styles.planned}`}>
        {labels[status] || status}
      </span>
    );
  };

  const auditTypeLabels = {
    initial: "İlk Denetim",
    surveillance: "Gözetim Denetimi",
    recertification: "Yeniden Belgelendirme",
    special: "Özel Denetim"
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-[#1A4D2E] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!audit) return null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-detail">
      <Button variant="ghost" onClick={() => navigate("/audits")} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Denetimlere Dön
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <Card className="lg:col-span-2 border-[#E8F3EE]">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl bg-[#1A4D2E] flex items-center justify-center">
                  <ClipboardList className="w-8 h-8 text-white" />
                </div>
                <div>
                  <CardTitle className="text-2xl text-[#111827]">{audit.title}</CardTitle>
                  <p className="text-[#4B5563] mt-1">{auditTypeLabels[audit.audit_type] || audit.audit_type}</p>
                </div>
              </div>
              {getStatusBadge(audit.status)}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-4 rounded-lg bg-[#F9FAFB]">
                <Building2 className="w-5 h-5 text-[#1A4D2E]" />
                <div>
                  <p className="text-xs text-[#9CA3AF]">Müşteri</p>
                  <p className="font-medium text-[#111827]">{audit.customer_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 rounded-lg bg-[#F9FAFB]">
                <Calendar className="w-5 h-5 text-[#1A4D2E]" />
                <div>
                  <p className="text-xs text-[#9CA3AF]">Tarih</p>
                  <p className="font-medium text-[#111827]">
                    {format(new Date(audit.scheduled_date), "dd MMMM yyyy", { locale: tr })}
                    {audit.end_date && ` - ${format(new Date(audit.end_date), "dd MMMM yyyy", { locale: tr })}`}
                  </p>
                </div>
              </div>
              {audit.location && (
                <div className="flex items-center gap-3 p-4 rounded-lg bg-[#F9FAFB]">
                  <MapPin className="w-5 h-5 text-[#1A4D2E]" />
                  <div>
                    <p className="text-xs text-[#9CA3AF]">Konum</p>
                    <p className="font-medium text-[#111827]">{audit.location}</p>
                  </div>
                </div>
              )}
              {audit.assigned_auditor_name && (
                <div className="flex items-center gap-3 p-4 rounded-lg bg-[#F9FAFB]">
                  <User className="w-5 h-5 text-[#1A4D2E]" />
                  <div>
                    <p className="text-xs text-[#9CA3AF]">Denetçi</p>
                    <p className="font-medium text-[#111827]">{audit.assigned_auditor_name}</p>
                  </div>
                </div>
              )}
            </div>

            {audit.description && (
              <div className="p-4 rounded-lg bg-[#F9FAFB]">
                <p className="text-xs text-[#9CA3AF] mb-2">Açıklama</p>
                <p className="text-[#4B5563]">{audit.description}</p>
              </div>
            )}

            {/* Status Change */}
            {(user?.role === "admin" || user?.role === "auditor") && (
              <div className="p-4 rounded-lg border border-[#E8F3EE]">
                <p className="text-sm font-medium text-[#111827] mb-3">Durum Güncelle</p>
                <Select value={audit.status} onValueChange={handleStatusChange}>
                  <SelectTrigger className="w-full md:w-[200px]" data-testid="audit-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planned">Planlandı</SelectItem>
                    <SelectItem value="in_progress">Devam Ediyor</SelectItem>
                    <SelectItem value="completed">Tamamlandı</SelectItem>
                    <SelectItem value="cancelled">İptal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Reports */}
          <Card className="border-[#E8F3EE]">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-[#111827]">Raporlar</CardTitle>
                {tenantSlug !== 'royalcert' && (
                  <Button
                    size="sm"
                    className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90"
                    onClick={() => navigate(`/reports?audit=${id}`)}
                    data-testid="create-report-btn"
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    Rapor Yaz
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {/* Determine stage based on customer_audit_phase OR audit_type */}
              {(() => {
                const customerPhase = parseInt(audit?.customer_audit_phase) || 2;
                const auditType = audit?.audit_type || '';
                // Check if audit_type indicates stage 3
                const isStage3Audit = auditType.includes('stage3') || auditType.includes('_3') || customerPhase === 3;
                const isStage2Audit = !isStage3Audit;
                
                return (
                  <>
                    {/* RoyalCert specific Stage 2 (TR-I) Form - Only show for Stage 2 */}
                    {tenantSlug === 'royalcert' && isStage2Audit && (
                      <div className="mb-4 p-4 rounded-lg bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                              <ClipboardCheck className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <p className="font-medium text-[#111827]">TR-I Denetim Raporu</p>
                              <p className="text-xs text-[#4B5563]">2. Aşama Rapor + Checklist + Kanıtlar</p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700"
                            onClick={() => {
                              // Role-based navigation for Stage 2
                              if (user?.role === 'planning_expert') {
                                // Planlama Uzmanı: Sadece Belgeler ve Kanıtlar
                                navigate(`/audit/royalcert/${id}?view=documents`);
                              } else {
                                // Denetçi, Admin, Süper Admin: Tam rapor sayfası
                                navigate(`/audit/royalcert/${id}`);
                              }
                            }}
                            data-testid="royalcert-stage2-form-btn"
                          >
                            Formu Aç
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* RoyalCert specific GSTC Audit Form - Only show for Stage 3 */}
                    {tenantSlug === 'royalcert' && isStage3Audit && (
                      <div className="mb-4 p-4 rounded-lg bg-gradient-to-r from-[#E8F3EE] to-[#F0F9F4] border border-[#1A4D2E]/20">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-[#1A4D2E] flex items-center justify-center">
                              <ClipboardCheck className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <p className="font-medium text-[#111827]">GSTC Denetim Formu</p>
                              <p className="text-xs text-[#4B5563]">3. Aşama Checklist + Rapor + Kanıtlar</p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90"
                            onClick={() => navigate(`/audit/royalcert/${id}`)}
                            data-testid="royalcert-audit-form-btn"
                          >
                            Formu Aç
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}

              {/* RoyalCert Man-Day Calculator */}
              {tenantSlug === 'royalcert' && (
                <div className="mb-4 p-4 rounded-lg bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-600 flex items-center justify-center">
                        <Calculator className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-[#111827]">Adam-Gün Hesaplama</p>
                        <p className="text-xs text-[#4B5563]">TL.01 - Denetim süresi hesaplayıcı</p>
                        {audit?.final_duration_days && (
                          <p className="text-sm text-purple-600 font-medium mt-1">
                            Mevcut: {audit.final_duration_days} A/G
                          </p>
                        )}
                      </div>
                    </div>
                    <ManDayCalculator 
                      roomCount={audit?.room_count || ""}
                      onCalculationComplete={handleManDayCalculation}
                      triggerButton={
                        <Button
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700"
                          data-testid="manday-calculator-btn"
                        >
                          {audit?.final_duration_days ? "Yeniden Hesapla" : "Hesapla"}
                        </Button>
                      }
                    />
                  </div>
                </div>
              )}
              
              {reports.length === 0 ? (
                <p className="text-[#9CA3AF] text-sm text-center py-4">Henüz rapor yok</p>
              ) : (
                <div className="space-y-2">
                  {reports.map((report) => (
                    <div
                      key={report.report_id}
                      className="p-3 rounded-lg bg-[#F9FAFB] hover:bg-[#E8F3EE] cursor-pointer transition-colors"
                      onClick={() => navigate(report.type === 'interactive' ? `/interactive-report/${report.report_id}` : `/reports/${report.report_id}`)}
                    >
                      <p className="font-medium text-[#111827] text-sm">{report.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          report.status === 'approved' ? 'bg-green-100 text-green-800' :
                          report.status === 'rejected' ? 'bg-red-100 text-red-800' :
                          report.status === 'submitted' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {report.status === 'draft' && 'Taslak'}
                          {report.status === 'submitted' && 'Onay Bekliyor'}
                          {report.status === 'approved' && 'Onaylandı'}
                          {report.status === 'rejected' && 'Reddedildi'}
                          {report.status === 'under_review' && 'İnceleniyor'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Templates */}
          <Card className="border-[#E8F3EE]">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-[#111827]">Şablonlar</CardTitle>
            </CardHeader>
            <CardContent>
              {templates.length === 0 ? (
                <p className="text-[#9CA3AF] text-sm text-center py-4">Şablon bulunamadı</p>
              ) : (
                <div className="space-y-2">
                  {templates.map((template) => (
                    <button
                      key={template.template_id}
                      onClick={() => downloadTemplate(template.template_id, template.file_name)}
                      className="w-full p-3 rounded-lg bg-[#F9FAFB] hover:bg-[#E8F3EE] transition-colors text-left"
                      data-testid={`download-template-${template.template_id}`}
                    >
                      <p className="font-medium text-[#111827] text-sm">{template.name}</p>
                      <p className="text-xs text-[#9CA3AF] mt-1">{template.file_type.toUpperCase()}</p>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AuditDetail;
